import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  CheckboxDropdown,
  Segmented,
  Switch,
  TagInput,
} from "../../../components/ui";
import LanguageOptions, { LANGUAGES } from "../../../components/LanguageOptions";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import { getAllGenres } from "../../../services/genres";
import { readImageFile } from "../../../utils/files";
import "../ads/ad-form.css";
import "../catalogue/content-form.css";
import "./channel-form.css";

/** Latencies of an encoder channel, with the key of their explanation. */
const LATENCIES = [
  { value: "default", label: "Default", hint: "default latency" },
  { value: "reduced", label: "Reduced", hint: "reduced latency" },
  { value: "low", label: "Low", hint: "low latency" },
];

/** JW tags ending with this suffix are managed by the API and hidden. */
const CHANNEL_TAG = "-channel";

/** Form values of a channel (`null`: new channel). */
export const channelFormValues = (channel) => ({
  channel_type: channel?.channel_type || "encoder",
  title: channel?.title || "",
  description: channel?.description || "",
  status: channel ? Boolean(channel.status) : true,
  dvr: channel ? channel.dvr === "on" : true,
  latency:
    channel?.latency && channel.latency !== "default latency"
      ? channel.latency
      : "",
  m3u_link: channel?.m3u_link || "",
  language: channel?.default_language || "",
  language_code: channel?.language_code || "",
  genres: (channel?.genre || []).map((genre) => genre._id),
  seo_tags: channel?.seo_tags || [],
  jw_tags: (channel?.jw_tags || []).filter((tag) => !tag.endsWith(CHANNEL_TAG)),
  content_type: channel?.content_type || "default",
  price: channel?.price ?? 0,
  monetization: Boolean(channel?.monetization),
  imageBase64: "",
  bannerUrl: channel?.thumbnail?.static_thumbnail_url || "",
});

/**
 * Body of the channel requests. The update does not take the banner, sent
 * apart (`updateChannelThumbnail`).
 */
export const channelPayload = (values, { withBanner }) => {
  const encoder = values.channel_type === "encoder";
  return {
    ...(withBanner && {
      imageBase64: values.imageBase64,
      isThumbnailSelected: Boolean(values.imageBase64),
    }),
    title: values.title,
    description: values.description,
    channel_type: values.channel_type,
    status: values.status,
    dvr: encoder ? (values.dvr ? "on" : "off") : "",
    latency: encoder ? values.latency : "",
    m3u_link: encoder ? "" : values.m3u_link,
    genres: values.genres,
    seo_tags: values.seo_tags,
    jw_tags: values.jw_tags,
    content_type: values.content_type,
    monetization: values.monetization,
    default_language: values.language,
    language_code: values.language_code,
    category: "channel",
    price:
      values.content_type === "exclusive" && values.price !== ""
        ? values.price
        : 0,
  };
};

/**
 * Validation of a channel: required fields with at least one genre, a
 * latency (encoder) or a link (M3U), a price for exclusive content and a
 * banner for an active channel.
 */
export const channelError = (payload, { banner }) => {
  const optional = ["imageBase64", "isThumbnailSelected", "price", "dvr", "latency", "m3u_link"];
  const missing = Object.entries(payload).some(
    ([key, value]) =>
      !optional.includes(key) &&
      (value === null ||
        value === undefined ||
        value === "" ||
        value === "default" ||
        (key === "genres" && value.length === 0))
  );
  const linkMissing =
    payload.channel_type === "encoder" ? !payload.latency : !payload.m3u_link;
  if (missing || linkMissing) return "Please Fill Channel Info!";
  if (payload.content_type === "exclusive" && !(payload.price > 0)) {
    return "Please enter a price for exclusive content!";
  }
  if (payload.status && !banner) return "Please upload banner image!";
  return null;
};

/**
 * Add / edit form of a live channel. `onSubmit(values)` performs the
 * requests once the values are valid (see `channelError`).
 */
const ChannelForm = ({
  initialValues,
  withBanner,
  onSubmit,
  submitLabel,
  cancelPath,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [genres, setGenres] = useState([]);
  const [bannerName, setBannerName] = useState("");
  const [saving, setSaving] = useState(false);
  // The page may remount the form once saved (fresh values).
  const mounted = useRef(true);
  useEffect(() => () => (mounted.current = false), []);
  useEffect(() => {
    getAllGenres("live channels").then((result) => setGenres(result || []));
  }, []);
  const set = (field) => (value) =>
    setValues((current) => ({ ...current, [field]: value }));

  const encoder = values.channel_type === "encoder";
  const banner = values.imageBase64 || values.bannerUrl;
  const latencyHint = LATENCIES.find((item) => item.value === values.latency);

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Genres that were disabled since are dropped.
    const submitted =
      genres.length > 0
        ? {
            ...values,
            genres: values.genres.filter((id) =>
              genres.some((genre) => genre._id === id)
            ),
          }
        : values;
    const error = channelError(channelPayload(submitted, { withBanner }), {
      banner,
    });
    if (error) {
      toast.error(t(error));
      return;
    }
    setSaving(true);
    try {
      await onSubmit(submitted);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <form className="content-form" onSubmit={handleSubmit}>
      <div className="content-form-grid">
        <section className="ui-card">
          <div className="ui-form content-form-body">
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("Channel Type")}</span>
              <Segmented
                label={t("Channel Type")}
                value={values.channel_type}
                onChange={set("channel_type")}
                options={[
                  { value: "encoder", label: t("Encoder") },
                  { value: "m3u", label: "M3U" },
                ]}
              />
            </div>
            <div className="ui-field ui-field-full">
              <label htmlFor="channel-title">{t("Title")}</label>
              <input
                id="channel-title"
                className="ui-input"
                value={values.title}
                onChange={(e) => set("title")(e.target.value)}
              />
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("desc")}</span>
              <ReactQuill
                className="ui-editor"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                value={values.description}
                onChange={set("description")}
              />
            </div>
            {encoder ? (
              <div className="ui-field ui-field-full">
                <label htmlFor="channel-latency">{t("Latency")}</label>
                <select
                  id="channel-latency"
                  className="ui-input"
                  value={values.latency || "none"}
                  onChange={(e) => set("latency")(e.target.value)}
                >
                  <option disabled value="none">
                    {t("Choose Latency")}
                  </option>
                  {LATENCIES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {t(item.label)}
                    </option>
                  ))}
                </select>
                {latencyHint && (
                  <span className="ui-hint">{t(latencyHint.hint)}</span>
                )}
              </div>
            ) : (
              <div className="ui-field ui-field-full">
                <label htmlFor="channel-m3u">{t("M3U link")}</label>
                <input
                  id="channel-m3u"
                  className="ui-input"
                  type="url"
                  placeholder="https://…/playlist.m3u8"
                  value={values.m3u_link}
                  onChange={(e) => set("m3u_link")(e.target.value)}
                />
              </div>
            )}
            <div className="ui-field">
              <label htmlFor="channel-language">{t("Default Language")}</label>
              <select
                id="channel-language"
                className="ui-input"
                value={
                  values.language === "all"
                    ? "all all"
                    : LANGUAGES.find(({ value }) =>
                        value.startsWith(`${values.language} `)
                      )?.value || "default"
                }
                onChange={(e) => {
                  const [language, code] = e.target.value.split(" ");
                  setValues((current) => ({
                    ...current,
                    language,
                    language_code: code,
                  }));
                }}
              >
                <option disabled value="default">
                  {t("choose default lang")}
                </option>
                <option value="all all">{t("All")}</option>
                <LanguageOptions />
              </select>
            </div>
            <div className="ui-field">
              <span className="ui-label">{t("Genre")}</span>
              <CheckboxDropdown
                label={t("Choose genres")}
                options={genres.map((genre) => genre.name)}
                value={genres
                  .filter((genre) => values.genres.includes(genre._id))
                  .map((genre) => genre.name)}
                onChange={(names) =>
                  set("genres")(
                    genres
                      .filter((genre) => names.includes(genre.name))
                      .map((genre) => genre._id)
                  )
                }
              />
            </div>
            <div className="ui-field">
              <label htmlFor="channel-tags">{t("tags")}</label>
              <TagInput
                id="channel-tags"
                value={values.jw_tags}
                onChange={set("jw_tags")}
                placeholder={t("Type and press Enter")}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="channel-seo">{t("Seo Keywords")}</label>
              <TagInput
                id="channel-seo"
                value={values.seo_tags}
                onChange={set("seo_tags")}
                placeholder={t("Type and press Enter")}
              />
            </div>
          </div>
        </section>

        <div className="content-form-side">
          <section className="ui-card content-side-card">
            <h2 className="content-side-title">{t("Banner")}</h2>
            <div className="content-banner">
              {banner ? (
                <img src={banner} alt="" />
              ) : (
                <span className="ad-banner-empty" aria-hidden="true">
                  <i className="ri-image-line"></i>
                </span>
              )}
            </div>
            <div className="content-banner-actions">
              <label htmlFor="channel-banner" className="ad-upload">
                <i className="ri-upload-2-line" aria-hidden="true"></i>
                {bannerName || t("upload banner")}
              </label>
              <input
                id="channel-banner"
                type="file"
                accept=".png, .jpg, .jpeg"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  setBannerName(file.name);
                  readImageFile(file, set("imageBase64"));
                  e.target.value = "";
                }}
              />
              {values.imageBase64 && (
                <button
                  type="button"
                  className="ui-icon-btn"
                  data-tone="danger"
                  title={t("Delete")}
                  aria-label={t("Delete")}
                  onClick={() => {
                    set("imageBase64")("");
                    setBannerName("");
                  }}
                >
                  <i className="ri-delete-bin-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </section>

          <section className="ui-card content-side-card">
            <h2 className="content-side-title">{t("Publication")}</h2>
            <div className="content-setting">
              <span>{t("status")}</span>
              <Switch
                checked={values.status}
                onChange={set("status")}
                label={t("status")}
                onLabel={t("Active")}
                offLabel={t("Inactive")}
              />
            </div>
            {encoder && (
              <div className="content-setting">
                <span>{t("DVR")}</span>
                <Switch
                  checked={values.dvr}
                  onChange={set("dvr")}
                  label={t("DVR")}
                  onLabel={t("On")}
                  offLabel={t("Off")}
                />
              </div>
            )}
            <div className="content-setting">
              <span>{t("Monetization")}</span>
              <Switch
                checked={values.monetization}
                onChange={set("monetization")}
                label={t("Monetization")}
                onLabel={t("On")}
                offLabel={t("Off")}
              />
            </div>
            <div className="ui-field">
              <label htmlFor="channel-content-type">{t("content type")}</label>
              <select
                id="channel-content-type"
                className="ui-input"
                value={values.content_type}
                onChange={(e) => set("content_type")(e.target.value)}
              >
                <option disabled value="default">
                  {t("content type")}
                </option>
                <option value="free">{t("Free")}</option>
                <option value="subscription">{t("Subscription")}</option>
                <option value="exclusive">{t("Exclusive")}</option>
              </select>
            </div>
            {values.content_type === "exclusive" && (
              <div className="ui-field">
                <label htmlFor="channel-price">{t("Price")}</label>
                <div className="ui-input-group">
                  <input
                    id="channel-price"
                    className="ui-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={Number.isNaN(values.price) ? "" : values.price}
                    onChange={(e) => set("price")(parseFloat(e.target.value))}
                  />
                  <span className="ui-input-addon" aria-hidden="true">
                    $
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="content-form-footer">
        <Link to={cancelPath} className="ui-btn ui-btn-secondary">
          {t("cancel")}
        </Link>
        <button type="submit" className="ui-btn" disabled={saving}>
          {saving && <span className="ad-spinner" aria-hidden="true" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ChannelForm;
